import { TOKEN_STORAGE_TIME } from "./../api/axiosApi";
// REACT IMPORTS
import { useMemo } from "react";
import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";

// COMPONENT IMPORTS
import { RootState } from "./../store";
import { useAppSelector } from "store/hooks";
import {
  forgotPassword,
  getAccountDetails,
  getEmailIds,
  getPhoneNumbers,
  getSignUpDetails,
  login,
  googleLogin,
  requestAccess,
  resetPassword,
  setNewPassword,
  userSignUp,
  verificationSend,
  contactOtpSend,
  verifyOtp,
  updatePhone,
  updateEmail,
  updateAccountData,
  getNotification,
  readNotification,
  getAccountPermission,
  getCustomerDetail,
  addActivity,
  uploadProfilePicture,
  checkToken,
  refreshToken,
  getFinancialSettings,
  getProfilePicture,
  getAccountAttach,
  getNotificationCount,
} from "store/services/authServices";
import { TOKEN_STORAGE_KEY } from "store/api/axiosApi";
import { UserData } from "types/authTypes";
import { AccountPermissionStatus } from "utils/customerPortalPermissions";

interface InitialState {
  accountDetails: any;
  userData: UserData | any;
  emailOptions: any;
  phoneNumberOptions: any;
  signUpDetails: any;
  isReadAllNotification?: boolean;
  accountPermission: any;
  accountPermissionStatus: AccountPermissionStatus;
  accountPermissionClusterId: string | null;
  accountPermissionRequestId: string | null;
  showOverlay: boolean;
  isExpiry: boolean;
  isExpired: boolean;
  isTokenRequire: boolean;
  isTokenRefreshing: boolean;
  financialSettings: any;
  profilePicture: string | null;
  notificationCount: number | null;
}

const initialState: InitialState = {
  accountDetails: {},
  userData: {},
  emailOptions: [],
  phoneNumberOptions: [],
  signUpDetails: {},
  isReadAllNotification: false,
  accountPermission: {},
  accountPermissionStatus: "idle",
  accountPermissionClusterId: null,
  accountPermissionRequestId: null,
  showOverlay: false,
  isExpiry: false,
  isExpired: false,
  isTokenRequire: false,
  isTokenRefreshing: false,
  financialSettings: {},
  profilePicture: null,
  notificationCount: null,
};

// A non-2xx answer carries { status, message, body: { success, data: { msg } } }.
// Rejecting with that body (instead of the serialized axios error) lets the
// login screens show the server's reason; network errors still throw.
const rejectWithServerError = (
  error: any,
  rejectWithValue: (value: any) => any
) => {
  if (error?.response?.data) {
    return rejectWithValue(error.response.data);
  }
  throw error;
};

export const accountDetailsGet = createAsyncThunk(
  "account-details/get",
  async (params: any) => {
    return await getAccountDetails(params);
  }
);

export const phoneNumbersGet = createAsyncThunk(
  "phone-numbers/get",
  async (params: any, { rejectWithValue }) => {
    try {
      return await getPhoneNumbers(params);
    } catch (error) {
      return rejectWithServerError(error, rejectWithValue);
    }
  }
);

export const emailIdsGet = createAsyncThunk(
  "email-ids/get",
  async (params: any, { rejectWithValue }) => {
    try {
      return await getEmailIds(params);
    } catch (error) {
      return rejectWithServerError(error, rejectWithValue);
    }
  }
);

export const userLogin = createAsyncThunk(
  "user/login",
  async (payload: any, { rejectWithValue }) => {
    try {
      return await login(payload);
    } catch (error) {
      return rejectWithServerError(error, rejectWithValue);
    }
  }
);

export const userGoogleLogin = createAsyncThunk(
  "user/google-login",
  async (payload: any, { rejectWithValue }) => {
    try {
      return await googleLogin(payload);
    } catch (error) {
      return rejectWithServerError(error, rejectWithValue);
    }
  }
);

export const sendVerification = createAsyncThunk(
  "verification-send/code",
  async (payload: any, { rejectWithValue }) => {
    try {
      return await verificationSend(payload);
    } catch (error) {
      return rejectWithServerError(error, rejectWithValue);
    }
  }
);

export const contactVerification = createAsyncThunk(
  "contact-verification-send/code",
  async (payload: any) => {
    return await contactOtpSend(payload);
  }
);

export const phoneUpdate = createAsyncThunk(
  "contact-data-update/phone",
  async (payload: any) => {
    return await updatePhone(payload);
  }
);

export const emailUpdate = createAsyncThunk(
  "contact-data-update/phone",
  async (payload: any) => {
    return await updateEmail(payload);
  }
);

export const accountDataUpdate = createAsyncThunk(
  "contact-data-update/data",
  async (payload: any) => {
    return await updateAccountData(payload);
  }
);

export const otpVerify = createAsyncThunk(
  "otp-verify/code",
  async (payload: any, { rejectWithValue }) => {
    try {
      return await verifyOtp(payload);
    } catch (error) {
      return rejectWithServerError(error, rejectWithValue);
    }
  }
);

export const signUpDetailsGet = createAsyncThunk(
  "signup-details/get",
  async (params: any) => {
    return await getSignUpDetails(params);
  }
);

export const signUpUser = createAsyncThunk(
  "signup/user",
  async (params: any) => {
    return await userSignUp(params);
  }
);

export const newPasswordSet = createAsyncThunk(
  "set-password/user",
  async ({ otpToken, ...payload }: any) => {
    return await setNewPassword(payload, otpToken);
  }
);

export const passwordForgot = createAsyncThunk(
  "forgot-password/user",
  async (payload: any) => {
    return await forgotPassword(payload);
  }
);

export const passwordReset = createAsyncThunk(
  "reset-password/user",
  async ({ otpToken, ...payload }: any) => {
    return await resetPassword(payload, otpToken);
  }
);

export const accessRequest = createAsyncThunk(
  "access-request/user",
  async (payload: any) => {
    return await requestAccess(payload);
  }
);

export const notificationGet = createAsyncThunk(
  "get-notification/user",
  async (params: any) => {
    return await getNotification(params);
  }
);

export const notificationCountGet = createAsyncThunk(
  "get-notification-count/user",
  async (params: any) => {
    return await getNotificationCount(params);
  }
);

export const notificationRead = createAsyncThunk(
  "read-notification/user",
  async (payload: any) => {
    return await readNotification(payload);
  }
);

export const addNotification = createAsyncThunk(
  "customer-detail/add-notification",
  async (payload: any) => {
    return await addActivity(payload);
  }
);

export const accountPermissionGet = createAsyncThunk(
  "account-permission/get",
  async (params: any) => {
    return await getAccountPermission(params);
  }
);

export const customerDetailGet = createAsyncThunk(
  "customer-detail/get",
  async (params: any) => {
    return await getCustomerDetail(params);
  }
);

export const updateProfilePicture = createAsyncThunk(
  "user-profile-image/upload",
  async (payload: any) => {
    return await uploadProfilePicture(payload);
  }
);

const profilePictureGet = createAsyncThunk(
  "profile-picture/get",
  async (params: any) => {
    return await getProfilePicture(params);
  }
);

export const checkTokenExpire = createAsyncThunk(
  "check-token/expire",
  async (params: any) => {
    return await checkToken(params);
  }
);

export const refreshTokenExpire = createAsyncThunk(
  "refresh-token/expire",
  async (params: any) => {
    return await refreshToken(params);
  }
);

export const financialSettingsGet = createAsyncThunk(
  "financial-account-settings/get",
  async (params: any) => {
    return await getFinancialSettings(params);
  }
);

export const accountAttachGet = createAsyncThunk(
  "account-attach/get",
  async (payload: any, { rejectWithValue }) => {
    try {
      return await getAccountAttach(payload);
    } catch (error) {
      return rejectWithServerError(error, rejectWithValue);
    }
  }
);
// While a one-time password is outstanding, login returns only the account
// id; the profile arrives once the code is verified. A guest logging in on a
// payment page already has that account's details from the link, so those are
// kept rather than replaced.
const withPendingAccount = (state: InitialState, body: any) => {
  if (!(body?.success && body?.data?.otpRequired)) {
    return body;
  }
  const current = state.userData?.data?.accountData;
  const pending = body.data.accountData;
  const isSameAccount =
    !!pending?.id &&
    [current?.id, current?.accounts_id].some(
      (id) => id != null && String(id) === String(pending.id)
    );
  return {
    ...body,
    data: {
      ...body.data,
      accountData: isSameAccount ? { ...current, ...pending } : pending,
    },
  };
};

/* ------------------------------------- AUTH SLICE ------------------------------------- */

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    clearAuthSlice: (state) => {
      for (const key in initialState) {
        if (initialState.hasOwnProperty(key)) {
          const typedKey = key as keyof typeof initialState;
          state[typedKey] = initialState[typedKey];
        }
      }
    },
    updateUserData: (state, action) => {
      state.userData = {
        ...state.userData,
        data: { ...state?.userData?.data, accountData: action.payload },
      };
    },
    updateTokenStatus: (state, action) => {
      state.isTokenRequire = action.payload;
      state.isExpiry = action.payload;
    },
    updateTokenExpiredStatus: (state, action) => {
      state.isExpired = action.payload;
    },
    updateOverlayState: (state, action) => {
      state.showOverlay = action.payload;
    },
    updateNotificationCount: (state, action) => {
      state.notificationCount = action.payload;
    },
    updateToken: (state, action) => {
      state.userData = {
        ...state.userData,
        token: action.payload?.token || null,
      };
    },
    updateIsReadAllNotification: (state, action) => {
      state.isReadAllNotification = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      checkTokenExpire.fulfilled,
      (state, action: PayloadAction<any>) => {
        if (action?.payload?.success) {
          state.isExpiry = action?.payload?.isExpiry;
          state.isTokenRequire = action?.payload?.isTokenRequire;
        }
      }
    );
    builder.addCase(refreshTokenExpire.pending, (state, action) => {
      state.isTokenRefreshing = true;
    });
    builder.addCase(refreshTokenExpire.fulfilled, (state, action: any) => {
      state.isTokenRefreshing = false;
      if (action?.payload?.success) {
        const token = action?.payload?.token;
        state.isExpiry = false;
        state.isTokenRequire = false;
        localStorage.setItem(TOKEN_STORAGE_KEY, token);
        state.userData = {
          ...state.userData,
          token,
        };
        localStorage.setItem(TOKEN_STORAGE_TIME, `${new Date().getTime()}`);
      }
    });
    builder.addCase(refreshTokenExpire.rejected, (state, action) => {
      state.isTokenRefreshing = false;
    });
    builder.addCase(
      accountDetailsGet.fulfilled,
      (state, action: PayloadAction<any>) => {
        if (action.payload?.body?.success) {
          let data = {
            ...action.payload?.body,
            data: {
              ...action.payload?.body?.data,
              auth_fields: action.payload?.body?.data?.auth_fields?.split(","),
            },
          };
          state.accountDetails = data;
        } else {
          state.accountDetails = action.payload?.body;
        }
      }
    );

    builder.addCase(
      userLogin.fulfilled,
      (state, action: PayloadAction<any>) => {
        state.userData = withPendingAccount(state, action.payload?.body);
      }
    );

    builder.addCase(
      userGoogleLogin.fulfilled,
      (state, action: PayloadAction<any>) => {
        if (action.payload?.body?.token) {
          state.userData = withPendingAccount(state, action.payload?.body);
        }
      }
    );

    builder
      .addCase(accountPermissionGet.pending, (state, action) => {
        state.accountPermission = {};
        state.accountPermissionStatus = "loading";
        state.accountPermissionClusterId = String(
          action.meta.arg?.cluster_id || ""
        );
        state.accountPermissionRequestId = action.meta.requestId;
      })
      .addCase(accountPermissionGet.fulfilled, (state, action: any) => {
        if (state.accountPermissionRequestId !== action.meta.requestId) {
          return;
        }

        state.accountPermission = action.payload?.body.data || {};
        state.accountPermissionStatus = "succeeded";
        state.accountPermissionClusterId = String(
          action.meta.arg?.cluster_id || ""
        );
        state.accountPermissionRequestId = null;
      })
      .addCase(accountPermissionGet.rejected, (state, action) => {
        if (state.accountPermissionRequestId !== action.meta.requestId) {
          return;
        }

        state.accountPermission = {};
        state.accountPermissionStatus = "failed";
        state.accountPermissionClusterId = String(
          action.meta.arg?.cluster_id || ""
        );
        state.accountPermissionRequestId = null;
      });

    builder.addCase(
      signUpDetailsGet.fulfilled,
      (state, action: PayloadAction<any>) => {
        state.signUpDetails = action.payload?.body;
      }
    );

    builder.addCase(
      phoneNumbersGet.fulfilled,
      (state, action: PayloadAction<any>) => {
        let data = action.payload?.body?.data?.phones || [];
        state.phoneNumberOptions = data?.map((val: any) => {
          return {
            name: `${"+" + val?.phone_code + " " + val?.phones}`,
            value: val?.id,
          };
        });
      }
    );

    builder.addCase(
      passwordReset.fulfilled,
      (state, action: PayloadAction<any>) => {
        state.userData = action.payload?.body;
      }
    );

    builder.addCase(
      newPasswordSet.fulfilled,
      (state, action: PayloadAction<any>) => {
        state.userData = action.payload?.body;
      }
    );

    builder.addCase(
      emailIdsGet.fulfilled,
      (state, action: PayloadAction<any>) => {
        let data = action.payload?.body?.data?.emails || [];
        state.emailOptions = data?.map((val: any) => {
          return {
            name: val?.email,
            value: val?.id,
          };
        });
      }
    );

    builder.addCase(
      financialSettingsGet.fulfilled,
      (state, action: PayloadAction<any>) => {
        if (action?.payload?.body?.success) {
          state.financialSettings = action?.payload?.body?.data;
        }
      }
    );

    builder.addCase(
      profilePictureGet.fulfilled,
      (state, action: PayloadAction<any>) => {
        if (action?.payload?.body?.success) {
          state.profilePicture = action?.payload?.body?.data?.profileBase64;
        }
      }
    );

    builder.addCase(
      notificationCountGet.fulfilled,
      (state, action: PayloadAction<any>) => {
        if (action?.payload?.body?.status) {
          state.notificationCount =
            action?.payload?.body?.data?.unreadCount || null;
        }
      }
    );
  },
});

export const {
  clearAuthSlice,
  updateUserData,
  updateTokenExpiredStatus,
  updateTokenStatus,
  updateOverlayState,
  updateNotificationCount,
  updateToken,
  updateIsReadAllNotification,
} = authSlice.actions;
export default authSlice.reducer;

const selectAccountDetails = (state: RootState) =>
  state.auth.accountDetails;
export const useAccountDetails = () => {
  const accountDetails = useAppSelector(selectAccountDetails);
  return useMemo(() => accountDetails, [accountDetails]);
};

const selectUserData = (state: RootState) => state.auth.userData;
export const useUserData = () => {
  const userData = useAppSelector(selectUserData);
  return useMemo(() => userData, [userData]);
};

const selectPhoneNumberOptions = (state: RootState) =>
  state.auth.phoneNumberOptions;
export const usePhoneNumberOptions = () => {
  const phoneNumberOptions = useAppSelector(selectPhoneNumberOptions);
  return useMemo(() => phoneNumberOptions, [phoneNumberOptions]);
};

const selectEmailOptions = (state: RootState) => state.auth.emailOptions;
export const useEmailOptions = () => {
  const emailOptions = useAppSelector(selectEmailOptions);
  return useMemo(() => emailOptions, [emailOptions]);
};

const selectSignUpDetails = (state: RootState) =>
  state.auth.signUpDetails;
const useSignUpDetails = () => {
  const signUpDetails = useAppSelector(selectSignUpDetails);
  return useMemo(() => signUpDetails, [signUpDetails]);
};
